import { getDb } from "@/services/storage/sqlite/connection";
import { createId } from "@/utils";
import type { Project, ProjectLink, ProjectLinkEntityType } from "@/types";

interface ProjectRow {
  id: string;
  workspace_id: string;
  name: string;
  description: string;
  tags_json: string;
  archived: number;
  created_at: number;
  updated_at: number;
}

interface ProjectLinkRow {
  id: string;
  project_id: string;
  entity_type: ProjectLinkEntityType;
  entity_id: string;
  created_at: number;
}

function rowToProject(row: ProjectRow): Project {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    name: row.name,
    description: row.description,
    tags: JSON.parse(row.tags_json) as string[],
    archived: row.archived === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToLink(row: ProjectLinkRow): ProjectLink {
  return {
    id: row.id,
    projectId: row.project_id,
    entityType: row.entity_type,
    entityId: row.entity_id,
    createdAt: row.created_at,
  };
}

export const projectRepository = {
  async list(workspaceId: string): Promise<Project[]> {
    const db = await getDb();
    const rows = await db.select<ProjectRow[]>(
      "SELECT * FROM projects WHERE workspace_id = $1 ORDER BY archived ASC, updated_at DESC",
      [workspaceId],
    );
    return rows.map(rowToProject);
  },

  async get(id: string): Promise<Project | null> {
    const db = await getDb();
    const rows = await db.select<ProjectRow[]>("SELECT * FROM projects WHERE id = $1", [id]);
    return rows[0] ? rowToProject(rows[0]) : null;
  },

  async create(input: { workspaceId: string; name: string; description?: string }): Promise<Project> {
    const db = await getDb();
    const now = Date.now();
    const project: Project = {
      id: createId("proj"),
      workspaceId: input.workspaceId,
      name: input.name,
      description: input.description ?? "",
      tags: [],
      archived: false,
      createdAt: now,
      updatedAt: now,
    };
    await db.execute(
      `INSERT INTO projects (id, workspace_id, name, description, tags_json, archived, created_at, updated_at)
       VALUES ($1, $2, $3, $4, $5, 0, $6, $7)`,
      [project.id, project.workspaceId, project.name, project.description, JSON.stringify(project.tags), now, now],
    );
    return project;
  },

  async update(id: string, patch: { name?: string; description?: string; tags?: string[] }): Promise<void> {
    const db = await getDb();
    const rows = await db.select<ProjectRow[]>("SELECT * FROM projects WHERE id = $1", [id]);
    const existing = rows[0];
    if (!existing) return;
    const name = patch.name ?? existing.name;
    const description = patch.description ?? existing.description;
    const tags = patch.tags ?? (JSON.parse(existing.tags_json) as string[]);
    await db.execute("UPDATE projects SET name = $1, description = $2, tags_json = $3, updated_at = $4 WHERE id = $5", [
      name,
      description,
      JSON.stringify(tags),
      Date.now(),
      id,
    ]);
  },

  async setArchived(id: string, archived: boolean): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE projects SET archived = $1, updated_at = $2 WHERE id = $3", [
      archived ? 1 : 0,
      Date.now(),
      id,
    ]);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM projects WHERE id = $1", [id]);
  },

  // --- Links -----------------------------------------------------------------

  async listLinks(projectId: string): Promise<ProjectLink[]> {
    const db = await getDb();
    const rows = await db.select<ProjectLinkRow[]>("SELECT * FROM project_links WHERE project_id = $1", [projectId]);
    return rows.map(rowToLink);
  },

  async addLink(projectId: string, entityType: ProjectLinkEntityType, entityId: string): Promise<ProjectLink> {
    const db = await getDb();
    const link: ProjectLink = { id: createId("plnk"), projectId, entityType, entityId, createdAt: Date.now() };
    await db.execute(
      "INSERT INTO project_links (id, project_id, entity_type, entity_id, created_at) VALUES ($1, $2, $3, $4, $5)",
      [link.id, link.projectId, link.entityType, link.entityId, link.createdAt],
    );
    return link;
  },

  async removeLink(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM project_links WHERE id = $1", [id]);
  },
};
